import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw, Clock, MessageSquare } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Remarketing = () => {
  const { darkMode } = useSettings();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [remarketingStats, setRemarketingStats] = useState({
    elegiveis_agora: 0,
    enviados: 0,
    entregues: 0,
    erros: 0,
    total_eventos: 0
  });

  const [scheduler, setScheduler] = useState({
    habilitado: false,
    intervalo_minutos: 5,
    horario_inicio: 8,
    horario_fim: 20
  });

  useEffect(() => {
    fetchLeadsForRemarketing();
  }, []);

  const fetchLeadsForRemarketing = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/remarketing-overview`
      );

      setLeads(
        Array.isArray(response.data?.leads)
          ? response.data.leads
          : []
      );

      setRemarketingStats({
        elegiveis_agora:
          Number(response.data?.stats?.elegiveis_agora || 0),
        enviados:
          Number(response.data?.stats?.enviados || 0),
        entregues:
          Number(response.data?.stats?.entregues || 0),
        erros:
          Number(response.data?.stats?.erros || 0),
        total_eventos:
          Number(response.data?.stats?.total_eventos || 0)
      });

      setScheduler({
        habilitado:
          response.data?.scheduler?.habilitado === true,
        intervalo_minutos:
          Number(response.data?.scheduler?.intervalo_minutos || 5),
        horario_inicio:
          Number(response.data?.scheduler?.horario_inicio || 8),
        horario_fim:
          Number(response.data?.scheduler?.horario_fim || 20)
      });
    } catch (error) {
      console.error('Erro ao carregar Remarketing real:', error);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Remarketing</h1>
        <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Leads que entraram no anúncio mas não fecharam contrato</p>
      </div>

      {/* Cards reais do PostgreSQL */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <div className="flex items-center gap-4">
            <div className="bg-yellow-100 p-3 rounded-lg">
              <Clock className="text-yellow-600" size={24} />
            </div>
            <div>
              <p className={darkMode ? 'text-gray-400 text-sm' : 'text-gray-500 text-sm'}>
                Elegíveis agora
              </p>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {remarketingStats.elegiveis_agora}
              </p>
            </div>
          </div>
        </div>

        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <div className="flex items-center gap-4">
            <div className="bg-blue-100 p-3 rounded-lg">
              <MessageSquare className="text-blue-600" size={24} />
            </div>
            <div>
              <p className={darkMode ? 'text-gray-400 text-sm' : 'text-gray-500 text-sm'}>
                Mensagens enviadas
              </p>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {remarketingStats.enviados}
              </p>
            </div>
          </div>
        </div>

        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <div className="flex items-center gap-4">
            <div className="bg-green-100 p-3 rounded-lg">
              <RefreshCw className="text-green-600" size={24} />
            </div>
            <div>
              <p className={darkMode ? 'text-gray-400 text-sm' : 'text-gray-500 text-sm'}>
                Entregues
              </p>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {remarketingStats.entregues}
              </p>
            </div>
          </div>
        </div>

        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <div className="flex items-center gap-4">
            <div className="bg-red-100 p-3 rounded-lg">
              <MessageSquare className="text-red-600" size={24} />
            </div>
            <div>
              <p className={darkMode ? 'text-gray-400 text-sm' : 'text-gray-500 text-sm'}>
                Erros de envio
              </p>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {remarketingStats.erros}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className={`rounded-lg border px-4 py-3 text-sm ${
        scheduler.habilitado
          ? 'border-green-300 bg-green-50 text-green-700'
          : 'border-yellow-300 bg-yellow-50 text-yellow-700'
      }`}>
        {scheduler.habilitado
          ? `Automação ativa das ${scheduler.horario_inicio}h às ${scheduler.horario_fim}h.`
          : 'Automação desativada durante a homologação.'}
      </div>

      {/* Leads Table */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr className={`text-left text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <th className="px-6 py-3">Nome</th>
                <th className="px-6 py-3">CPF/CNPJ</th>
                <th className="px-6 py-3">Telefone</th>
                <th className="px-6 py-3">Status Atual</th>
                <th className="px-6 py-3">Data Criação</th>
                <th className="px-6 py-3">Tentativas</th>
                <th className="px-6 py-3">Próximo gatilho</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id} className={`border-t ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                  <td className={`px-6 py-4 font-medium ${darkMode ? 'text-white' : ''}`}>{lead.nome}</td>
                  <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{lead.cpf_cnpj}</td>
                  <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{lead.telefone}</td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      {lead.status}
                    </span>
                  </td>
                  <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {new Date(lead.data_criacao).toLocaleDateString('pt-BR')}
                  </td>
                  <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {lead.numero_tentativas || 0}
                  </td>
                  <td className="px-6 py-4">
                      <span className="font-medium text-blue-600">
                        {lead.trigger ? `${lead.trigger}h` : '-'}
                      </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {leads.length === 0 && (
          <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Nenhum lead está elegível para remarketing neste momento.
          </div>
        )}
      </div>
    </div>
  );
};

export default Remarketing;
